/**
 * Formata textos de enums para exibição padronizada
 */
export function formatEnumText(text: string | null | undefined): string {
  if (!text) return '';
  
  // Converte de UPPER_CASE ou kebab-case para Title Case
  return text
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Formata tipos de família de produto
 */
export function formatFamilia(familia: string): string {
  const familiaMap: Record<string, string> = {
    'Móvel': 'Móvel',
    'Fibra': 'Fibra',
    'Fixa Básica': 'Fixa Básica',
    'Fixa Avançada': 'Fixa Avançada',
    'TI': 'TI',
    'Aparelhos': 'Aparelhos',
    'Energia': 'Energia',
    'Totalização': 'Totalização',
    'Outro': 'Outro'
  };
  
  return familiaMap[familia] || formatEnumText(familia);
}

/**
 * Formata tipos de faturamento
 */
export function formatFaturamentoTipo(tipo: string): string {
  const tipoMap: Record<string, string> = {
    'VENDA NOVA': 'Venda Nova',
    'MIGRAÇÃO': 'Migração',
    'APARELHO': 'Aparelho',
    'ENERGIA': 'Energia',
    'NÃO PAGO': 'Não Pago',
    'OUTRO': 'Outro'
  };
  
  return tipoMap[tipo] || formatEnumText(tipo);
}

/**
 * Formata o código do pedido no padrão #P{numero}
 * Suporta formato antigo (7 dígitos) e novo (10 dígitos AAMMDDSSSS)
 * @param numeroPedido - Número sequencial do pedido
 * @returns String formatada (ex: #P2510060051 ou #P2500045)
 */
export function formatPedidoCode(numeroPedido: number): string {
  // Converter para inteiro caso venha como notação científica
  const numeroInteiro = Math.floor(Number(numeroPedido));
  
  // Validar se é um número válido
  if (!Number.isFinite(numeroInteiro) || numeroInteiro <= 0) {
    console.error('⚠️ numeroPedido inválido:', numeroPedido);
    return '#P?';
  }
  
  return `#P${numeroInteiro}`;
}

/**
 * Formata o código do item no padrão #P{numero}-{seq}
 * @param numeroPedido - Número sequencial do pedido
 * @param seq - Sequência do item no pedido
 * @returns String formatada (ex: #P2510060051-1)
 */
export function formatItemCode(numeroPedido: number, seq: number): string {
  const numeroInteiro = Math.floor(numeroPedido);
  return `#P${numeroInteiro}-${seq}`;
}

/**
 * Converte numero_pedido de notação científica para inteiro
 * @param numero - Número do pedido (pode estar em notação científica)
 * @returns Número inteiro
 */
export function parseNumeroPedido(numero: any): number {
  if (!numero) return 0;
  // Sempre converte para Number primeiro para lidar com notação científica
  // Ex: "2.51021005e+09" → 2510210050
  return Math.floor(Number(numero));
}

/**
 * Parse detalhado do número do pedido (para debug/logs)
 * Suporta formato antigo (7 dígitos: AASSSSS) e novo (10 dígitos: AAMMDDSSSS)
 * @param numeroPedido - Número do pedido
 * @returns Objeto com breakdown do número
 */
export function parsePedidoCodeDetailed(numeroPedido: number): {
  formato: 'antigo' | 'novo';
  ano?: number;
  mes?: number;
  dia?: number;
  sequencial: number;
} {
  const str = numeroPedido.toString();
  
  if (str.length === 10) {
    // Novo formato: AAMMDDSSSS
    return {
      formato: 'novo',
      ano: parseInt(str.substring(0, 2), 10),
      mes: parseInt(str.substring(2, 4), 10),
      dia: parseInt(str.substring(4, 6), 10),
      sequencial: parseInt(str.substring(6, 10), 10)
    };
  } else if (str.length === 7) {
    // Formato antigo: AASSSSS
    return {
      formato: 'antigo',
      ano: parseInt(str.substring(0, 2), 10),
      sequencial: parseInt(str.substring(2, 7), 10)
    };
  } else {
    return {
      formato: 'antigo',
      sequencial: numeroPedido
    };
  }
}

/**
 * Formata tempo relativo de forma compacta
 * @param data - String ISO de data
 * @returns String formatada (ex: "2h", "3d", "1w")
 */
export function formatarTempoRelativo(data: string): string {
  const agora = new Date();
  const dataPassada = new Date(data);
  const diffMs = agora.getTime() - dataPassada.getTime();
  
  const minutos = Math.floor(diffMs / 60000);
  const horas = Math.floor(diffMs / 3600000);
  const dias = Math.floor(diffMs / 86400000);
  const semanas = Math.floor(diffMs / 604800000);
  
  if (minutos < 1) {
    return 'agora';
  } else if (minutos < 60) {
    return `${minutos}m`;
  } else if (horas < 24) {
    return `${horas}h`;
  } else if (dias < 7) {
    return `${dias}d`;
  } else {
    return `${semanas}s`; // semanas
  }
}

/**
 * Formata valores monetários em formato brasileiro
 * @param value - Valor numérico
 * @returns String formatada (ex: R$ 1.234,56)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Formata número para exibição monetária no input
 * @param value - Valor numérico (ex: 255.50)
 * @returns String formatada (ex: "R$ 255,50")
 */
export function formatCurrencyInput(value: number | null | undefined): string {
  if (!value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Parse de string monetária para número
 * @param text - Texto do input (ex: "R$ 255,50" ou "255,50" ou "25550")
 * @returns Número decimal (ex: 255.50)
 */
export function parseCurrencyInput(text: string): number {
  if (!text) return 0;
  
  // Remove símbolos e espaços
  let cleaned = text.replace(/[R$\s]/g, '');
  
  // Se tem vírgula mas não tem ponto, considera vírgula como decimal
  if (cleaned.includes(',') && !cleaned.includes('.')) {
    cleaned = cleaned.replace(',', '.');
  } else if (cleaned.includes('.') && cleaned.includes(',')) {
    // Se tem ambos, remove ponto (milhar) e troca vírgula por ponto
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Normaliza telefone brasileiro removendo +55, formatação e zeros extras
 * Retorna apenas os dígitos (10 ou 11 dígitos)
 * @param input - Telefone em qualquer formato
 * @returns String com apenas dígitos normalizados
 */
export function normalizarTelefoneBR(input: string): string {
  if (!input) return '';
  
  // Remove tudo que não é dígito
  let digitos = input.replace(/\D/g, '');
  
  // Se começar com 55 e tiver 12+ dígitos, remove o código do país
  if (digitos.startsWith('55') && digitos.length >= 12) {
    digitos = digitos.substring(2);
  }
  
  // Se ainda tiver mais de 11 dígitos, pegar apenas os últimos 11
  if (digitos.length > 11) {
    digitos = digitos.slice(-11);
  }
  
  return digitos;
}

/**
 * Valida se telefone normalizado está válido (10 ou 11 dígitos)
 * @param telefoneNormalizado - Telefone já normalizado (só dígitos)
 * @returns Objeto com validação e mensagem de erro
 */
export function validarTelefoneBR(telefoneNormalizado: string): { 
  valido: boolean; 
  erro?: string;
} {
  const len = telefoneNormalizado.length;
  
  if (len === 0) {
    return { valido: false, erro: 'Informe o número' };
  }
  
  if (len < 10) {
    return { valido: false, erro: `Faltam ${10 - len} dígitos (mín. 10 com DDD)` };
  }
  
  if (len === 10 || len === 11) {
    return { valido: true };
  }
  
  return { valido: false, erro: `Dígitos demais (${len}). Use apenas DDD + número` };
}
